import React, { useEffect, useRef, useState } from "react";
import {
  Award,
  Building2,
  CheckCircle2,
  Factory,
  Landmark,
  MapPin,
  ShieldCheck,
  Star,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;

    const el = ref.current;
    if (!el) return;

    let observer: IntersectionObserver | null = null;

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer?.disconnect();
        }
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px -20% 0px",
        ...(options || {}),
      }
    );

    observer.observe(el);
    return () => observer?.disconnect();
  }, [inView, options]);

  return { ref, inView };
}

const Reveal: React.FC<{
  children: React.ReactNode;
  variant?: "fadeUp" | "fadeLeft" | "fadeRight" | "scale";
  delayMs?: number;
  className?: string;
}> = ({ children, variant = "fadeUp", delayMs = 0, className = "" }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const delay = delayMs ? { transitionDelay: `${delayMs}ms` } : undefined;

  const hidden: Record<string, string> = {
    fadeUp: "opacity-0 translate-y-6",
    fadeLeft: "opacity-0 -translate-x-6",
    fadeRight: "opacity-0 translate-x-6",
    scale: "opacity-0 scale-95",
  };

  return (
    <div
      ref={ref}
      style={delay}
      className={`${className} transition-all duration-700 ease-out will-change-transform will-change-opacity ${
        inView ? "opacity-100 translate-y-0 translate-x-0 scale-100" : hidden[variant]
      }`}
    >
      {children}
    </div>
  );
};

const iconMap: Record<string, React.ElementType> = {
  Award,
  Building2,
  CheckCircle2,
  Factory,
  Landmark,
  MapPin,
  ShieldCheck,
  Star,
};

const ConstructIntroduction: React.FC<{ content?: any }> = ({ content }) => {
  const { isArabic } = useLanguage();
  const kicker = content?.kicker || (isArabic ? "مجموعة حمدان - عمان" : "Hamdan Group - Oman");
  const title = content?.title || (isArabic ? "قطاع الإنشاءات" : "CONSTRUCTION SECTOR");
  const desc =
    content?.desc ||
    (isArabic
      ? "يعتبر قطاع الإنشاءات في مجموعة حمدان أحد أبرز الأقسام، حيث يعمل في مشاريع البناء المدنية والخاصة واسعة النطاق في جميع أنحاء سلطنة عمان."
      : "The construction sector of Hamdan Group is one of the most prominent divisions, engaged in large scale civil and private construction activities across the Sultanate of Oman.");
  const introText = content?.introText || "";
  const image = content?.introImage || "/Hamdan-Construction.png";
  const noteTitle = content?.noteTitle || (isArabic ? "ملاحظة سريعة" : "Quick Note");
  const noteDesc =
    content?.noteDesc ||
    (isArabic ? "قدرات إنشائية واسعة النطاق للمشاريع المدنية والخاصة في سلطنة عمان." : "Large-scale civil and private construction capabilities across Oman.");
  const imageBadgeTitle = content?.imageBadgeTitle || (isArabic ? "مقدمة" : "Introduction");
  const imageLabel = (content?.imageLabel || (isArabic ? "حمدان للانشاءات" : "Hamdan Construction")).replace(
    /^Introduction\s+/i,
    ""
  );
  const panelTitle = content?.panelTitle || (isArabic ? "مقدمة" : "INTRODUCTION");
  const panelFooter =
    content?.panelFooter === "Details based on company profile content."
      ? ""
      : content?.panelFooter || "";
  const capabilitiesTitle =
    content?.capabilitiesTitle || (isArabic ? "قدرات الإنشاءات" : "Construction Capabilities");
  const capabilitiesMeta = content?.capabilitiesMeta || (isArabic ? "مدني - خاص - حكومي" : "Civil - Private - Public");
  const brandDesc =
    content?.brandDesc ||
    (isArabic ? "قسم إنشائي يركز على التنفيذ الموثوق، والهندسة العملية، والقيمة المستدامة." : "A construction division focused on reliable execution, practical engineering, and long-term service value.");
  const brandTags =
    content?.brandTags && content.brandTags.length > 0
      ? content.brandTags
      : (isArabic ? ["إنشاءات", "بنية تحتية", "عمان"] : ["Construction", "Infrastructure", "Oman"]);
  const titleWords = String(title).trim().split(/\s+/);
  const titleFirstLine = titleWords[0] || title;
  const titleSecondLine = titleWords.slice(1).join(" ");

  const points =
    content?.points && content.points.length > 0
      ? content.points
      : [
          {
            title: isArabic ? "الإنشاءات المدنية" : "Civil Construction",
            desc: isArabic ? "مشاريع رئيسية في صلالة وجنوب عمان." : "Major works across Salalah and South Oman.",
            icon: "Building2",
          },
          {
            title: isArabic ? "تسليم معتمد" : "Certified Delivery",
            desc: isArabic ? "تنفيذ موثوق للمشاريع الحكومية والخاصة." : "Trusted execution for public and private sector projects.",
            icon: "ShieldCheck",
          },
          {
            title: isArabic ? "مشاريع بارزة" : "Landmark Projects",
            desc: isArabic ? "أعمال البناء السكني والبنية التحتية والتجارية." : "Housing, infrastructure and commercial construction work.",
            icon: "Landmark",
          },
        ];

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <Reveal variant="fadeLeft" className="lg:col-span-3">
            <div className="sticky top-6">
              <p className="text-xs uppercase tracking-widest text-gray-500">
                {kicker}
              </p>

              <h2 className="mt-3 font-semibold leading-tight text-gray-900 break-words">
                <span className="block text-[24px] md:text-[28px]">{titleFirstLine}</span>
                {titleSecondLine && (
                  <span className="block text-[30px] md:text-[34px] xl:text-[38px]">{titleSecondLine}</span>
                )}
              </h2>

              <div className="mt-6 space-y-2">
                {points.slice(0, 3).map((point: any, idx: number) => (
                  <div
                    key={`${point.title || "point"}-${idx}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full border bg-gray-50"
                  >
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    {point.title || point.desc}
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border p-4 bg-gray-50">
                <div className="text-xs uppercase tracking-wider text-gray-500">
                  {noteTitle}
                </div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                  {noteDesc}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal variant="scale" delayMs={120} className="lg:col-span-5">
            <div className="relative rounded-[28px] overflow-hidden border shadow-sm bg-gray-50">
              <img
                src={image}
                alt={title}
                className="w-full h-[460px] object-cover"
                onError={(event) => {
                  event.currentTarget.src = "/banner/banner4.jpg";
                }}
              />

              <div className="absolute left-5 top-5 bg-white/90 backdrop-blur px-4 py-3 rounded-2xl border shadow-sm">
                <div className="text-xs uppercase tracking-widest text-gray-600">
                  {imageBadgeTitle}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {imageLabel}
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-yellow-500 via-yellow-400 to-red-500" />
            </div>
          </Reveal>

          <Reveal variant="fadeRight" delayMs={200} className="lg:col-span-4">
            <div className="rounded-2xl border bg-gray-50 p-6">
              <h3 className="text-lg font-bold text-gray-900">
                {panelTitle}
              </h3>

              <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed text-justify">
                <div
                  dangerouslySetInnerHTML={{
                    __html: desc.replace(/\n/g, "<br/>"),
                  }}
                />
                {introText && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: introText.replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </div>

              {panelFooter && (
                <div className="mt-5 pt-4 border-t text-xs text-gray-500">
                  {panelFooter}
                </div>
              )}
            </div>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Reveal variant="fadeUp" className="md:col-span-2">
            <div className="rounded-2xl border bg-white p-6 shadow-sm h-full">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-lg font-bold text-gray-900">
                  {capabilitiesTitle}
                </h4>
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  {capabilitiesMeta}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {points.slice(0, 3).map((point: any, idx: number) => {
                  const Icon = iconMap[point.icon] || Building2;

                  return (
                    <div
                      key={`${point.title || "capability"}-${idx}`}
                      className="rounded-xl border bg-gray-50 px-4 py-4"
                    >
                      <div className="h-10 w-10 rounded-2xl bg-white border flex items-center justify-center text-red-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="mt-3 text-sm font-bold text-gray-900">
                        {point.title}
                      </div>
                      <p className="mt-2 text-sm text-gray-700 text-justify leading-relaxed">
                        {point.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal variant="fadeUp" delayMs={150}>
            <div className="rounded-2xl shadow-sm border h-full bg-white">
              <div className="flex h-full min-h-[220px] flex-col items-center justify-center p-6 text-center">
                <p className="max-w-md text-sm text-gray-700 leading-relaxed">
                  {brandDesc}
                </p>

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {brandTags.map((tag: string) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full border bg-gray-50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ConstructIntroduction;
